import $ST from "plugins/tfs/platformConfigs/staticTextMaps/basket";
import $R from "plugins/tfs/platformConfigs/integrationMaps/routes";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BasketItem from "../basketItem";
import { useDispatch, useSelector } from "react-redux";
import { getMiscProduct } from "actions/miscProduct";
import { uniqueId } from "lodash";

// pageContainer
import Basket from "_pageContainers/basket";

import PageTitle from "theme/PageTitle";
import Button from "theme/Button";
import TextBox from "theme/TextBox";
import Error from "theme/Error";
import Spinner from "theme/Spinner";
import Summary from "theme/basket/Summary";
import Widget from "theme/widgets";
import $S from "theme/settings";
import MiscProduct from "../miscProduct";

const newVoucherEntry = () => ({
  id: uniqueId("voucher_"),
  barcode: "",
  controlDigit: "",
  isApplied: false,
});

const BasketGrid = (pageContext) => {
  const dispatch = useDispatch();
  const {
    props: { basket, basketItems, customer, pending, error, logged_in },
    setQuantityData,
    applyVoucherCode,
  } = pageContext;
  const { product, success: miscProductFetchSuccess } = useSelector(
    (state) => state.miscProduct
  );

  const [inputList, setInputList] = useState([newVoucherEntry()]);

  useEffect(() => {
    if (basket.voucher_code && basket.voucher_code.trim()) {
      const appliedVouchers = basket.voucher_code
        .split(",")
        .filter(Boolean)
        .map((voucher) => {
          const parts = voucher.trim().split("|");
          return {
            id: uniqueId("voucher_"),
            barcode: parts[0] || "",
            controlDigit: parts[1] || "",
            isApplied: true,
          };
        });
      setInputList(appliedVouchers.length > 0 ? appliedVouchers : [newVoucherEntry()]);
    } else {
      setInputList([newVoucherEntry()]);
    }
  }, [basket.voucher_code]);

  const handleBarcodeChange = (val, id) => {
    setInputList(inputList.map((item) =>
      item.id === id ? { ...item, barcode: val } : item
    ));
  };

  const handleControlDigitChange = (val, id) => {
    setInputList(inputList.map((item) =>
      item.id === id ? { ...item, controlDigit: val } : item
    ));
  };

  const buildVoucherCode = (item) => {
    const barcode = item.barcode.trim();
    const controlDigit = item.controlDigit.trim();
    return controlDigit ? `${barcode}|${controlDigit}` : barcode;
  };

  const isDuplicateVoucher = (barcode, currentId) => {
    if (!barcode || !barcode.trim()) return false;
    const normalizedBarcode = barcode.trim().toUpperCase();
    return inputList.some((item) => {
      if (item.id === currentId) return false;
      return item.barcode && item.barcode.trim().toUpperCase() === normalizedBarcode;
    });
  };

  const getAppliedVouchersCount = () => {
    if (!basket.voucher_code || !basket.voucher_code.trim()) return 0;
    return basket.voucher_code.split(",").filter((v) => v && v.trim()).length;
  };

  const canAddNewInput = () => {
    const appliedCount = getAppliedVouchersCount();
    const appliedInputsCount = inputList.filter((item) => item.isApplied).length;
    return (
      appliedCount > 0 &&
      appliedCount === appliedInputsCount &&
      appliedCount === inputList.length
    );
  };

  const handleRemoveClick = (index) => {
    const itemToRemove = inputList[index];
    const voucherToRemove = buildVoucherCode(itemToRemove);

    const allAppliedVouchers = inputList
      .filter((item) => item.isApplied)
      .map(buildVoucherCode);

    const remainingAppliedVouchers = allAppliedVouchers.filter(
      (voucher) => voucher !== voucherToRemove
    );

    const list = [...inputList];
    list.splice(index, 1);

    if (list.length === 0) {
      setInputList([newVoucherEntry()]);
    } else {
      setInputList(list);
    }

    if (remainingAppliedVouchers.length === 0) {
      applyVoucherCode(true, []);
    } else {
      applyVoucherCode(false, remainingAppliedVouchers);
    }
  };

  const handleAddClick = () => {
    if (canAddNewInput()) {
      setInputList([...inputList, newVoucherEntry()]);
    }
  };

  const handleApplyClick = () => {
    const allVouchers = inputList
      .map(buildVoucherCode)
      .filter((code) => code && code.trim());
    applyVoucherCode(false, allVouchers);
  };

  const isValidBasketToFetchMiscProducts =
    basketItems.length === 1 &&
    basketItems[0].quantity === 1 &&
    +basketItems[0].product.attributes.integration_discount_percentage > 0 &&
    basketItems[0].product.attributes.extra_product_sku_instore;

  const renderBasketItems = () => {
    return basketItems.map((item, index) => {
      return (
        <BasketItem
          item={item}
          key={index}
          onQuantity={(quantity) => {
            const productAttributes = item.attributes || {};
            setQuantityData(quantity, item.product.pk, productAttributes);
          }}
        />
      );
    });
  };

  useEffect(() => {
    if (!miscProductFetchSuccess && isValidBasketToFetchMiscProducts) {
      const productSku =
        basketItems[0].product.attributes.extra_product_sku_instore;
      dispatch(getMiscProduct(productSku));
    }
  }, [basketItems, miscProductFetchSuccess]);

  return (
    <div>
      {!pending && basketItems.length === 0 && (
        <div className="basketEmptyMessage">{$ST.BASKET_EMPTY}</div>
      )}
      {pending && <Spinner />}
      {!pending && basketItems.length > 0 && (
        <div className="basket headerMargin">
          <div className="basket-header">
            <PageTitle classes="uppercase basket-header__pageTitle">
              {$ST.BASKET}
              {logged_in && (
                <span>
                  <span style={{ margin: "0 5px" }}>-</span>
                  {customer.first_name}&nbsp;{customer.last_name}
                </span>
              )}
            </PageTitle>
            <div className="basket-header__continue">
              <Link to={$R.MAIN_HOME.path}>{$ST.CONTINUE_SHOPPING}</Link>
            </div>
          </div>
          <div className="basket-items">{renderBasketItems()}</div>
          <div className="row justify-content-between align-items-start gap-5">
            {$S.hasBasketCoupon && (
              <div className="col-xs-12 col-sm-6 col-md-3">
                {inputList.map((x, i) => {
                  const isApplied = x.isApplied;
                  const isLastInput = i === inputList.length - 1;
                  const isFirstInput = i === 0;
                  const hasDuplicate = isDuplicateVoucher(x.barcode, x.id);

                  return (
                    <div className="coupon" key={x.id}>
                      {isFirstInput && (
                        <p className="title">{$ST.APPLY_DISCOUNT}</p>
                      )}
                      <p className="coupon-label">{$ST.BARCODE_LABEL}</p>
                      <TextBox
                        classes="uppercase"
                        name="barcode"
                        onChange={(val) => handleBarcodeChange(val, x.id)}
                        value={x.barcode || ""}
                        disabled={isApplied}
                      />
                      <p className="coupon-label">{$ST.CONTROL_DIGIT_LABEL}</p>
                      <TextBox
                        classes="uppercase"
                        name="control_digit"
                        onChange={(val) => handleControlDigitChange(val, x.id)}
                        value={x.controlDigit || ""}
                        disabled={isApplied}
                      />
                      {hasDuplicate && !isApplied && (
                        <Error
                          message={$ST.DUPLICATE_VOUCHER}
                          field="voucher_code"
                        />
                      )}
                      {isLastInput && (
                        <Error data={error} field="voucher_code" />
                      )}
                      {isApplied ? (
                        <Button
                          classes="button pointer red text-center"
                          onClick={() => handleRemoveClick(i)}
                          disabled={pending}
                        >
                          {$ST.REMOVE}
                        </Button>
                      ) : (
                        <Button
                          classes="button pointer text-center"
                          onClick={handleApplyClick}
                          disabled={
                            !x.barcode ||
                            !x.barcode.trim() ||
                            hasDuplicate ||
                            pending
                          }
                        >
                          {$ST.VOEG_HIER_TOE}
                        </Button>
                      )}
                      {i === inputList.length - 1 && canAddNewInput() && (
                        <Button
                          classes="button pointer text-center"
                          onClick={handleAddClick}
                          disabled={pending}
                        >
                          {$ST.ADD_VOUCHER}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <Widget path="basket.basket_messages" />
            {miscProductFetchSuccess && isValidBasketToFetchMiscProducts && (
              <MiscProduct product={product} />
            )}
            <div className="col-xs-12 col-sm-6 col-md-4">
              <Summary
                discounts={basket.discounts}
                total_amount={basket.total_amount}
                total_product_amount={basket.total_product_amount}
              />
              {logged_in && (
                <Link
                  className="button pointer text-center"
                  to={$R.CHECKOUT.path}
                >
                  {$ST.GO_TO_CHECKOUT}
                </Link>
              )}
              {!logged_in && (
                <div>
                  <Link
                    className="button pointer text-center"
                    to={{
                      pathname: $R.CUSTOMER_LIST.path,
                      state: { keepBasket: true },
                    }}
                  >
                    {$ST.PAY_WITH_CUSTOMER_ACCOUNT}
                  </Link>
                  {/* <Link
                    className='button white pointer text-center'
                    to={$R.NEW_USER.path}
                  >
                    {$ST.CREATE_NEW_USER}
                  </Link> */}
                  <Link
                    className="button white pointer text-center"
                    to={$R.GUEST.path}
                  >
                    {$ST.GUEST_CHECKOUT}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Basket(BasketGrid);



// WEBPACK FOOTER //
// ./src/plugins/tfs/pages/basket/basket/index.js